import {
  clearFeatureUsage,
  getAllFeatureUsage,
  putFeatureUsage,
} from "@db/featureUsage";
import { type FeatureCategory, featureRegistry } from "@lib/help";
import { create } from "zustand";

type CompletionCounts = {
  used: number;
  total: number;
};

type FeatureUsageStore = {
  usedFeatures: Set<string>;
  hydrated: boolean;
  hydrate: () => Promise<void>;
  markUsed: (featureId: string) => Promise<void>;
  isUsed: (featureId: string) => boolean;
  getCompletionByCategory: () => Map<FeatureCategory, CompletionCounts>;
  getOverallCompletion: () => CompletionCounts;
  clearAll: () => Promise<void>;
};

export const useFeatureUsageStore = create<FeatureUsageStore>((set, get) => ({
  usedFeatures: new Set(),
  hydrated: false,

  async hydrate() {
    const records = await getAllFeatureUsage();
    const usedFeatures = new Set(records.map((r) => r.featureId));
    set({ usedFeatures, hydrated: true });
  },

  async markUsed(featureId: string) {
    if (get().usedFeatures.has(featureId)) {
      return;
    }

    await putFeatureUsage({ featureId, usedAt: new Date() });

    set((state) => ({
      usedFeatures: new Set(state.usedFeatures).add(featureId),
    }));
  },

  isUsed(featureId: string) {
    return get().usedFeatures.has(featureId);
  },

  getCompletionByCategory() {
    const { usedFeatures } = get();
    const result = new Map<FeatureCategory, CompletionCounts>();

    for (const entry of featureRegistry) {
      const counts = result.get(entry.category) ?? { used: 0, total: 0 };
      counts.total++;

      if (usedFeatures.has(entry.id)) {
        counts.used++;
      }

      result.set(entry.category, counts);
    }

    return result;
  },

  getOverallCompletion() {
    const { usedFeatures } = get();
    let used = 0;

    for (const entry of featureRegistry) {
      if (usedFeatures.has(entry.id)) {
        used++;
      }
    }

    return { used, total: featureRegistry.length };
  },

  async clearAll() {
    await clearFeatureUsage();
    set({ usedFeatures: new Set() });
  },
}));
